import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Heart, CheckCircle2, ArrowLeft, ShieldCheck, Repeat } from "lucide-react";
import { toast } from "sonner";

// iter221 — Owner directive presets: $3 / $5 / $10 / Custom.
const PRESETS = [3, 5, 10];

export default function Tip() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [alreadyTipped, setAlreadyTipped] = useState(false);
  const [paidTip, setPaidTip] = useState(null);
  const [locked, setLocked] = useState(false); // iter105 — feedback gate
  const [amount, setAmount] = useState(5);
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // iter221 — Explicit opt-in for recurring/automatic tips on every
  // future completed service. Defaults OFF so one-time tipping never
  // accidentally enables recurring tips.
  const [authorizeRecurring, setAuthorizeRecurring] = useState(false);

  const sessionId = params.get("session_id");
  const justPaid = params.get("paid") === "1";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tips/booking/${bookingId}`);
        setBooking(data.booking);
        setAlreadyTipped(!!data.already_tipped);
        setPaidTip(data.tip || null);
        // iter105 — Only completed + paid visits allow tips
        const b = data.booking || {};
        const pay = (b.payment_status || "").toLowerCase();
        const isLocked =
          b.status !== "completed" ||
          !(pay === "paid" || pay === "pay_on_arrival") ||
          !!b.duplicate_of_booking_id ||
          !!b.archived_at;
        setLocked(isLocked);
      } catch (e) {
        toast.error("Booking not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [bookingId]);

  // After redirect from Stripe Checkout, poll the session status
  useEffect(() => {
    if (!sessionId || !justPaid) return;
    let cancel = false;
    let attempts = 0;
    const poll = async () => {
      if (cancel || attempts++ > 8) return;
      try {
        const { data } = await api.get(`/tips/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAlreadyTipped(true);
          toast.success("Thank you! Tip received.");
          return;
        }
      } catch { /* still polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId, justPaid]);

  const submitTip = async () => {
    const finalAmount = customMode ? parseFloat(customValue) : amount;
    if (!finalAmount || finalAmount <= 0) return toast.error("Pick or enter a tip amount");
    if (finalAmount > 500) return toast.error("Max tip is $500");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/tips/intent`, {
        booking_id: bookingId,
        amount: finalAmount,
        origin_url: window.location.origin,
        // iter221 — Only forward the authorization when the customer
        // explicitly ticked the box. Never inferred from the tip alone.
        authorize_recurring: !!authorizeRecurring,
      });
      if (data.paid) {
        setAlreadyTipped(true);
        setPaidTip(data.tip);
        if (data.recurring_tip_authorized) {
          toast.success(
            `Thank you! $${finalAmount.toFixed(2)} tip received — automatic tip is now ON.`,
          );
        } else {
          toast.success(`Thank you! $${finalAmount.toFixed(2)} tip received.`);
        }
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process tip");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#047857]" />
      </div>
    );
  }
  if (!booking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        Booking not found.
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="tip-page">
      <button
        onClick={() => navigate("/dashboard")}
        className="inline-flex items-center text-sm text-gray-500 hover:text-[#047857] mb-4"
        data-testid="tip-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
      </button>

      {alreadyTipped ? (
        <div className="bg-gradient-to-br from-[#064E3B] to-[#047857] text-white rounded-3xl p-8 text-center shadow-xl" data-testid="tip-thank-you">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-200" />
          <h1 className="mt-3 text-3xl font-extrabold font-[Outfit] tracking-tighter">Thank you!</h1>
          {paidTip?.amount && (
            <p className="mt-2 text-emerald-100">Your ${Number(paidTip.amount).toFixed(2)} tip means a lot to the crew.</p>
          )}
          <Button
            className="mt-6 rounded-full bg-white text-[#064E3B] hover:bg-emerald-50 font-bold w-full"
            onClick={() => navigate("/dashboard")}
            data-testid="tip-thank-dashboard"
          >
            Back to my dashboard
          </Button>
        </div>
      ) : locked ? (
        <div className="bg-white rounded-3xl border border-gray-200 p-7 shadow-sm text-center" data-testid="tip-locked">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto">
            <Heart className="w-7 h-7 text-gray-400" />
          </div>
          <h1 className="mt-3 text-2xl font-extrabold font-[Outfit] tracking-tight text-gray-900">Tipping unlocks after service</h1>
          <p className="mt-2 text-sm text-gray-600">
            You can leave a tip once your <span className="font-bold">{booking?.service_name || "service"}</span> visit is completed and paid.
          </p>
          <Button
            className="mt-6 rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-bold w-full"
            onClick={() => navigate("/dashboard")}
            data-testid="tip-locked-dashboard"
          >
            Back to my dashboard
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-gray-200 p-7 shadow-sm">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" />
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Tip the crew</div>
          </div>
          <h1 className="mt-1 text-3xl font-extrabold font-[Outfit] tracking-tighter text-gray-900">Loved the service?</h1>
          <p className="mt-1 text-sm text-gray-600">
            Show {booking?.service_name} crew some love — 100% goes to the team.
          </p>

          <div className="mt-6 grid grid-cols-4 gap-2" data-testid="tip-presets">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => { setAmount(p); setCustomMode(false); }}
                data-testid={`tip-preset-${p}`}
                className={`rounded-2xl border-2 py-4 font-extrabold text-lg tracking-tight transition-all ${!customMode && amount === p ? "border-[#047857] bg-emerald-50 text-[#047857]" : "border-gray-200 text-gray-700 hover:border-emerald-300"}`}
              >
                ${p}
              </button>
            ))}
            <button
              onClick={() => setCustomMode(true)}
              data-testid="tip-preset-custom"
              className={`rounded-2xl border-2 py-4 font-extrabold text-sm tracking-tight transition-all ${customMode ? "border-[#047857] bg-emerald-50 text-[#047857]" : "border-gray-200 text-gray-700 hover:border-emerald-300"}`}
            >
              Custom
            </button>
          </div>

          <div className="mt-3">
            {customMode && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-2xl font-extrabold text-gray-400">$</span>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  step="0.01"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder="0.00"
                  className="text-2xl font-extrabold h-14"
                  data-testid="tip-custom-amount"
                />
              </div>
            )}
          </div>

          {/* iter221 — Explicit "Add this tip automatically after every
              completed service" opt-in. The one-time tip and the
              automatic tip stay separate: ticking this box is the ONLY
              way recurring tips activate. */}
          {(() => {
            const displayAmount = customMode
              ? (parseFloat(customValue) || 0)
              : amount;
            return (
              <label
                className={`mt-4 flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                  authorizeRecurring
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-gray-200 bg-white hover:border-emerald-200"
                }`}
                data-testid="tip-authorize-recurring-block"
              >
                <input
                  type="checkbox"
                  checked={authorizeRecurring}
                  onChange={(e) => setAuthorizeRecurring(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded accent-emerald-700 flex-shrink-0"
                  data-testid="tip-authorize-recurring-checkbox"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Repeat className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                    <span className="font-extrabold text-gray-900 text-sm">
                      Add this tip automatically after every completed service
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-gray-700 leading-relaxed"
                        data-testid="tip-authorize-recurring-copy">
                    By checking this box, you authorize JDSN Lawn Care to
                    automatically add a{" "}
                    <span className="font-bold text-emerald-800">
                      ${displayAmount.toFixed(2)} tip
                    </span>{" "}
                    to every future completed service and charge it to your
                    saved payment method along with the service amount
                    (service + tip = total charge). You can turn this off
                    anytime from your JDSN account under Profile → Automatic
                    Tip.
                  </div>
                </div>
              </label>
            );
          })()}

          <Button
            className="mt-6 w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-6 text-lg shadow-lg"
            onClick={submitTip}
            disabled={submitting}
            data-testid="tip-submit"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                {authorizeRecurring ? "Send tip & turn on automatic tip · " : "Send tip of "}
                ${(customMode ? parseFloat(customValue || 0) : amount).toFixed(2)}
              </>
            )}
          </Button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-gray-500">
            <ShieldCheck className="w-3 h-3" />
            Secure payment via Stripe. Tipping is completely optional.
          </div>
        </div>
      )}
    </div>
  );
}
